import { Box } from "@mui/material";
import Loading from "components/common/Loading";
import EmailSentSuccessfully from "components/ForgotPassword/EmailSentSuccessfully";
import ExpiredLink from "components/ResetPassword/ExpiredLink";
import ResetPasswordForm from "components/ResetPassword/ResetPasswordForm";
import ResetPasswordSuccessfully from "components/ResetPassword/ResetPasswordSuccessfully";
import { auth } from "config/firebase.config";
import { EFirebaseErrorMessage } from "constants/firebaseError";
import { EPath } from "constants/path";
import { useToast } from "contexts/ToastContext";
import { AuthError, verifyPasswordResetCode } from "firebase/auth";
import { useForgotPassword } from "hooks/useForgotPassword";
import { useResetPassword } from "hooks/useResetPassword";
import { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router";

function ResetPasswordPage() {
  const { methods, onSubmit, isSubmitting, isSuccess } = useResetPassword();
  const {
    methods: forgotPasswordMethods,
    onSubmit: forgotPasswordOnSubmit,
    isSubmitting: forgotPasswordIsSubmitting,
    isSuccess: forgotPasswordIsSuccess,
  } = useForgotPassword();

  const [isExpired, setIsExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { state } = useLocation();
  const oobCode = state?.oobCode;
  const { showError } = useToast();
  const navigate = useNavigate();

  const validateOobCode = async (oobCode: string) => {
    try {
      setIsLoading(true);
      await verifyPasswordResetCode(auth, oobCode);
    } catch (error) {
      const authError = error as AuthError;
      handleValidateOobCodeError(authError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidateOobCodeError = (error: AuthError) => {
    switch (error.code) {
      case "auth/expired-action-code":
        setIsExpired(true);
        break;

      case "auth/invalid-action-code":
        showError(
          "Reset password failed",
          EFirebaseErrorMessage.INVALID_ACTION_CODE
        );
        navigate(EPath.LOGIN);
        break;

      case "auth/user-disabled":
        showError("Reset password failed", EFirebaseErrorMessage.USER_DISABLED);
        navigate(EPath.LOGIN);
        break;

      case "auth/user-not-found":
        showError(
          "Reset password failed",
          EFirebaseErrorMessage.USER_NOT_FOUND
        );
        navigate(EPath.LOGIN);
        break;

      default:
        showError(
          "Reset password failed",
          "An error occurred while resetting the password"
        );
        navigate(EPath.LOGIN);
        break;
    }
  };

  useEffect(() => {
    const processValidateOobCode = async () => {
      if (oobCode) {
        await validateOobCode(oobCode);
      }
    };
    void processValidateOobCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!oobCode) {
    return <Navigate to={EPath.LOGIN} />;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (isExpired) {
    return (
      <Box className="h-screen bg-color_F8F8F8 flex flex-col items-center justify-center">
        <Box className="flex flex-col items-center justify-center gap-4">
          {forgotPasswordIsSuccess ? (
            <EmailSentSuccessfully />
          ) : (
            <Box className="p-16 max-w-xl border bg-white rounded-xl space-y-6 border-color_E8E8E9 border-1">
              <FormProvider {...forgotPasswordMethods}>
                <form onSubmit={forgotPasswordOnSubmit} className="space-y-6">
                  <ExpiredLink
                    isValid={forgotPasswordMethods.formState.isValid}
                    isSubmitting={forgotPasswordIsSubmitting}
                  />
                </form>
              </FormProvider>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box className="h-screen bg-color_F8F8F8 flex flex-col items-center justify-center">
      <Box className="flex flex-col items-center justify-center gap-4">
        {isSuccess ? (
          <ResetPasswordSuccessfully />
        ) : (
          <Box className="p-16 max-w-xl border bg-white rounded-xl space-y-6 border-color_E8E8E9 border-1">
            <FormProvider {...methods}>
              <form
                onSubmit={methods.handleSubmit((data) =>
                  onSubmit(oobCode, data)
                )}
                className="space-y-6"
              >
                <ResetPasswordForm
                  isValid={methods.formState.isValid}
                  isSubmitting={isSubmitting}
                />
              </form>
            </FormProvider>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default ResetPasswordPage;
